import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Sparkles, Package, Check, X as XIcon, Clock,
  ChevronLeft, ChevronRight, Trash2, Download, Tag, LogOut
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import axios from "axios";

import { API, BACKEND_URL } from "../apiConfig";
import LOGO_URL from "../img/sapphire.svg";

const statusLabels = {
  pending: { label: "Pendiente", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  completed: { label: "Completado", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Check },
  cancelled: { label: "Cancelado", color: "bg-slate-100 text-slate-500 border-slate-200", icon: XIcon },
};

/* ─── Login Screen ────────────────────────────────────────── */
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem("admin_token", data.token);
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5F7] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Sapphire Spell" className="w-full mx-auto mb-3" />
          <h1 className="font-serif text-3xl font-medium text-slate-900 tracking-tight">Admin</h1>
          
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 space-y-5">
          {error && <p className="text-sm text-rose-600 bg-rose-50 rounded-xl p-3 text-center" data-testid="login-error">{error}</p>}
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">Email</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" className="rounded-full" data-testid="login-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">Contraseña</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder=" " className="rounded-full" data-testid="login-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full py-5" data-testid="login-submit">
            {loading ? "Entrando..." : "Iniciar sesión"}
          </Button>
        </form>
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">Volver al catálogo</Link>
        </div>
      </motion.div>
    </div>
  );
};

/* ─── Order Card ──────────────────────────────────────────── */
const OrderCard = ({ order, onStatusChange, onDelete, onLabelToggle, selected, onSelect }) => {
  const { label, color, icon: StatusIcon } = statusLabels[order.status] || statusLabels.pending;
  const date = new Date(order.created_at);
  const formattedDate = date.toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -50 }}
      className={`bg-white rounded-2xl border shadow-sm p-5 transition-colors ${selected ? "border-rose-300 ring-1 ring-rose-200" : "border-slate-100"}`}
      data-testid={`order-${order.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button onClick={() => onSelect(order.id)}
          className={`flex-shrink-0 w-5 h-5 mt-1 rounded border-2 flex items-center justify-center transition-all ${selected ? "bg-rose-500 border-rose-500" : "border-slate-300 hover:border-rose-300"}`}
          data-testid={`select-order-${order.id}`}>
          {selected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-serif text-lg text-slate-900">{order.client_name}</h3>
              <p className="text-xs text-slate-500">{formattedDate}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Label printed indicator */}
              <button
                onClick={() => onLabelToggle(order.id, !order.label_printed)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${order.label_printed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200 hover:border-emerald-200"}`}
                data-testid={`label-toggle-${order.id}`}>
                <Tag className="w-3 h-3" strokeWidth={1.5} />
                {order.label_printed ? "Etiqueta Impresa" : "Sin etiqueta"}
              </button>
              <Badge className={`${color} border text-xs rounded-full px-3 py-1`}>
                <StatusIcon className="w-3 h-3 mr-1" /> {label}
              </Badge>
            </div>
          </div>

          <div className="space-y-1 mb-4">
            {order.items.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-slate-700 truncate mr-2">{item.nombre} <span className="text-slate-400">({item.marca})</span></span>
                <span className="flex-shrink-0 font-medium text-slate-600">{item.size}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-3 border-t border-slate-100 flex-wrap">
            {order.status === "pending" && (
              <>
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => onStatusChange(order.id, "completed")} data-testid={`complete-order-${order.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Completar
                </Button>
                <Button size="sm" variant="ghost" className="rounded-full text-xs text-slate-500" onClick={() => onStatusChange(order.id, "cancelled")} data-testid={`cancel-order-${order.id}`}>
                  <XIcon className="w-3.5 h-3.5 mr-1" /> Cancelar
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" className="rounded-full text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50 ml-auto" onClick={() => onDelete(order.id)} data-testid={`delete-order-${order.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ─── Main Admin ──────────────────────────────────────────── */
const AdminOrders = () => {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ total: 0, pending: 0 });
  const [page, setPage] = useState(1);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const pageSize = 20;
  const navigate = useNavigate();

  const authHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Check existing session
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders(), withCredentials: true });
        setAdmin(data);
      } catch {
        setAdmin(null);
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const h = authHeaders();
      const [ordersRes, countsRes] = await Promise.all([
        axios.get(`${API}/orders?page=${page}&page_size=${pageSize}`, { headers: h, withCredentials: true }),
        axios.get(`${API}/orders/count`, { headers: h, withCredentials: true }),
      ]);
      setOrders(ordersRes.data);
      setCounts(countsRes.data);
    } catch (err) {
      if (err.response?.status === 401) { setAdmin(null); localStorage.removeItem("admin_token"); }
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { if (admin) fetchOrders(); }, [admin, fetchOrders]);

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem("admin_token");
    setAdmin(null);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/orders/${id}/status?status=${status}`, {}, { headers: authHeaders(), withCredentials: true });
      fetchOrders();
    } catch { toast.error("Error al actualizar"); }
  };

  const handleLabelToggle = async (id, printed) => {
    try {
      await axios.patch(`${API}/orders/${id}/label?printed=${printed}`, {}, { headers: authHeaders(), withCredentials: true });
      setOrders((prev) => prev.map((o) => o.id === id ? { ...o, label_printed: printed } : o));
    } catch { toast.error("Error al actualizar etiqueta"); }
  };

  const handleDeleteOne = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    try {
      await axios.delete(`${API}/orders/${id}`, { headers: authHeaders(), withCredentials: true });
      setSelectedOrders((s) => { const n = new Set(s); n.delete(id); return n; });
      fetchOrders();
      toast.success("Pedido eliminado");
    } catch { toast.error("Error al eliminar"); }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedOrders.size} pedido(s)?`)) return;
    try {
      await axios.post(`${API}/orders/bulk-delete`, { order_ids: [...selectedOrders] }, { headers: authHeaders(), withCredentials: true });
      setSelectedOrders(new Set());
      fetchOrders();
      toast.success("Pedidos eliminados");
    } catch { toast.error("Error al eliminar"); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("¿Eliminar TODOS los pedidos? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/orders`, { headers: authHeaders(), withCredentials: true });
      setSelectedOrders(new Set());
      fetchOrders();
      toast.success("Todos los pedidos eliminados");
    } catch { toast.error("Error"); }
  };

  const handleExport = () => {
    const token = localStorage.getItem("admin_token");
    window.open(`${API}/orders/export?token=${token}`, "_blank");
  };

  const toggleSelect = (id) => setSelectedOrders((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => {
    if (selectedOrders.size === orders.length) setSelectedOrders(new Set());
    else setSelectedOrders(new Set(orders.map((o) => o.id)));
  };

  if (checking) return <div className="min-h-screen bg-[#FFF5F7] flex items-center justify-center"><Skeleton className="w-40 h-6" /></div>;
  if (!admin) return <LoginScreen onLogin={setAdmin} />;

  const totalPages = Math.max(1, Math.ceil(counts.total / pageSize));

  return (
    <div className="min-h-screen bg-[#FFF5F7]">
      <header className="glass-header sticky top-0 z-50 border-b border-white/40 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors" data-testid="admin-back">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} /><span className="text-sm font-medium">Catálogo</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2"><img src={LOGO_URL} alt="Sapphire Spell" className="h-[72px] object-contain" /></Link>
              <button onClick={handleLogout} className="text-slate-500 hover:text-slate-700 transition-colors" data-testid="logout-btn"><LogOut className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-slate-900 tracking-tight flex items-center gap-3">
              <Package className="w-8 h-8 text-slate-700" strokeWidth={1.5} /> Pedidos
            </h1>
            <div className="flex gap-3 mt-3 flex-wrap">
              <span className="text-sm text-slate-500">{counts.total} totales</span>
              {counts.pending > 0 && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs">{counts.pending} pendientes</Badge>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="rounded-full" onClick={handleExport} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
            {selectedOrders.size > 0 && (
              <Button variant="outline" size="sm" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar ({selectedOrders.size})
              </Button>
            )}
            {orders.length > 0 && (
              <Button variant="ghost" size="sm" className="rounded-full text-rose-500" onClick={handleDeleteAll} data-testid="delete-all-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar todos
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" strokeWidth={1} />
            <p className="text-slate-500">No hay pedidos registrados aún</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <Button variant={selectedOrders.size === orders.length ? "secondary" : "outline"} size="sm" className="rounded-full text-sm" onClick={toggleAll} data-testid="select-all-orders">
                <Check className="w-4 h-4 mr-1.5" />
                {selectedOrders.size === orders.length ? "Deseleccionar todo" : "Seleccionar todo"}
              </Button>
            </div>

           <div className="space-y-4">
            <AnimatePresence>
              {orders.map((order) => (
                <OrderCard 
                  key={order._id || order.id} 
                  order={order}
                  onStatusChange={handleStatusChange} 
                  onDelete={handleDeleteOne}
                  onLabelToggle={handleLabelToggle}
                  selected={selectedOrders.has(order._id || order.id)} 
                  onSelect={toggleSelect} 
                />
              ))}
            </AnimatePresence>
          </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full" size="sm"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm text-slate-600">Página {page} de {totalPages}</span>
                <Button variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-full" size="sm"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminOrders;
